import { chain, map } from 'lodash'

import { getClient } from './auth'
import { matchGoogleId } from './util'

const BadIdError = function(spreadsheetId) {
  this.message = `No Google Sheet ID detected in "${spreadsheetId}"`
}
BadIdError.prototype = Object.create(Error.prototype)

const NotFoundError = function(googleId) {
  this.message = `No Google Sheet found for ID: "${googleId}"`
  this.googleId = googleId
  return this
}
NotFoundError.prototype = Object.create(Error.prototype)

const MAX_WORKSHEETS = 20

const api = {
  BadIdError, NotFoundError,

  matchGoogleId,

  fetchSheetById(spreadsheetId) {
    return new Promise((resolve, reject) => {
      let googleId = matchGoogleId(spreadsheetId)
      // error out from id parse failure
      if(!googleId){
        reject(new BadIdError(spreadsheetId))
        return
      }

      getClient((client) => {
        // worksheet names
        return client.sheets.spreadsheets.get({
          spreadsheetId: googleId
        }).then(({ result }) => {

          // extract spreadsheet name
          const spreadsheetName = result.properties.title,

            // extract worksheet ids
            worksheetIds = chain(result.sheets)
              .take(MAX_WORKSHEETS)
              .map("properties.sheetId")
              .invokeMap("toString")
              .value(),

            // extract worksheet titles
            worksheetTitles = chain(result.sheets)
              .take(MAX_WORKSHEETS)
              .map("properties.title")
              .value(),

            // construct batch query
            params = {
              spreadsheetId: googleId,
              // Convert sheet title to proper A1 notation
              ranges: worksheetTitles.map((sheetTitle) => {
                return `'${sheetTitle.replace(/'/g, "''")}'!A1:NA1000`
              })
            }

          return client.sheets.spreadsheets.values.batchGet(params)
            .then(({ result }) => {
              const worksheets = map(result.valueRanges, ({ values = [] }, index) => {
                return {
                  id: worksheetIds[index],
                  title: worksheetTitles[index],
                  values
                }
              })

              resolve({
                id:   result.spreadsheetId,
                name: spreadsheetName,
                worksheets
              })
            })
        }, (error) => {
          if(error.status == 404) {
            reject(new NotFoundError(googleId))
          } else {
            reject(new Error(error.result.error.message))
          }
        })
      })
    })
  },

  updateSheetRow(spreadsheetId, worksheetTitle, rowNumber, values) {
    return new Promise((resolve, reject) => {
      let googleId = matchGoogleId(spreadsheetId)
      if(!googleId) {
        reject(new BadIdError(spreadsheetId))
        return
      }

      getClient((client) => {
        const range = `'${worksheetTitle.replace(/'/g, "''")}'!A${rowNumber}`
        return client.sheets.spreadsheets.values.update({
          spreadsheetId: googleId,
          range,
          valueInputOption: 'USER_ENTERED',
          resource: { values: [values] }
        }).then(({ result }) => {
          resolve(result)
        }, (error) => {
          reject(new Error(error.result.error.message))
        })
      })
    })
  },

  appendSheetRow(spreadsheetId, worksheetTitle, values) {
    return new Promise((resolve, reject) => {
      let googleId = matchGoogleId(spreadsheetId)
      if(!googleId) { reject(new BadIdError(spreadsheetId)); return }
      getClient((client) => {
        const range = `'${worksheetTitle.replace(/'/g, "''")}'!A1`
        return client.sheets.spreadsheets.values.append({
          spreadsheetId: googleId,
          range,
          valueInputOption: 'USER_ENTERED',
          insertDataOption: 'INSERT_ROWS',
          resource: { values: [values] }
        }).then(({ result }) => resolve(result), (error) => reject(new Error(error.result.error.message)))
      })
    })
  },

  insertSheetRow(spreadsheetId, sheetId, rowIndex) {
    return new Promise((resolve, reject) => {
      let googleId = matchGoogleId(spreadsheetId)
      if(!googleId) { reject(new BadIdError(spreadsheetId)); return }
      getClient((client) => {
        return client.sheets.spreadsheets.batchUpdate({
          spreadsheetId: googleId,
          requests: [{
            insertDimension: {
              range: {
                sheetId: parseInt(sheetId),
                dimension: 'ROWS',
                startIndex: rowIndex,
                endIndex: rowIndex + 1
              },
              inheritFromBefore: true
            }
          }]
        }).then(({ result }) => resolve(result), (error) => reject(new Error(error.result.error.message)))
      })
    })
  },

  deleteSheetRow(spreadsheetId, sheetId, rowIndex) {
    return new Promise((resolve, reject) => {
      let googleId = matchGoogleId(spreadsheetId)
      if(!googleId) { reject(new BadIdError(spreadsheetId)); return }
      getClient((client) => {
        return client.sheets.spreadsheets.batchUpdate({
          spreadsheetId: googleId,
          requests: [{
            deleteDimension: {
              range: {
                sheetId: parseInt(sheetId),
                dimension: 'ROWS',
                startIndex: rowIndex,
                endIndex: rowIndex + 1
              }
            }
          }]
        }).then(({ result }) => resolve(result), (error) => reject(new Error(error.result.error.message)))
      })
    })
  },

  addSheetToSpreadsheet(spreadsheetId, sheetName) {
    return new Promise((resolve, reject) => {
      let googleId = matchGoogleId(spreadsheetId)
      // error out from id parse failure
      if(!googleId){
        reject(new BadIdError(spreadsheetId))
        return
      }

      getClient((client) => {
        // worksheet names
        const sheetId = Date.now() % 2000000000
        return client.sheets.spreadsheets.batchUpdate({
          spreadsheetId: googleId,
          requests: [
            {
              addSheet: {
                properties: {
                  title: sheetName,
                  index: 0,
                  sheetId
                }
              }
            }
          ]
        }).then(({ status, statusText }) => {
          if(status === 200){
            resolve(sheetId.toString())
          } else {
            reject(new Error(statusText))
          }
        })
      })
    })
  }
}

export default api
